<!--
  PaginationFilter — generic filter, sort, and pagination toolbar for Financial Close.
  Parent owns fetching and persistence. This component edits drafts and emits apply, save, and page-change.
  Slots: after-sort, after-count, before-pagination, toolbar-right. It does not call APIs.
  Filter and sort panels are position:fixed and moved to document.body while open so a transformed page parent cannot shrink the scrim.
-->
<template>
  <div class="pagination-filter" :data-testid="testIdPrefix" :aria-busy="loading ? 'true' : 'false'">
    <div class="pagination-filter__toolbar">
      <div class="pagination-filter__toolbar-left">
        <button ref="filterBtn" type="button" class="pagination-filter__btn" :data-testid="tid('filter-btn')" :disabled="disabled" @click="toggleFilter">
          <v-icon class="pagination-filter__glyph">mdi-filter-variant</v-icon>
          <span>Filters</span>
          <span v-if="activeFilterCount > 0" class="pagination-filter__badge" :data-testid="tid('filter-badge')">{{ activeFilterCount }}</span>
        </button>
        <button ref="sortBtn" type="button" class="pagination-filter__btn" :data-testid="tid('sort-btn')" :disabled="disabled" @click="toggleSort">
          <v-icon class="pagination-filter__glyph">mdi-sort</v-icon>
          <span>Sort</span>
          <span v-if="activeSortCount > 0" class="pagination-filter__badge" :data-testid="tid('sort-badge')">{{ activeSortCount }}</span>
        </button>
        <slot name="after-sort" />
      </div>

      <div class="pagination-filter__toolbar-right">
        <span class="pagination-filter__count" :data-testid="tid('record-count')">{{ formattedCount }} records</span>
        <slot name="after-count" />
        <slot name="before-pagination" />
        <div v-if="pageCount > 0" class="pagination-filter__pagination" :data-testid="tid('pagination')">
          <button type="button" class="pagination-filter__page-btn" :data-testid="tid('prev-btn')" aria-label="Previous page" :disabled="disabled || loading || page <= 1" @click="changePage(page - 1)">
            <v-icon class="pagination-filter__glyph">mdi-chevron-left</v-icon>
          </button>
          <span class="pagination-filter__page-info" :data-testid="tid('page-info')">
            Page <strong>{{ page }}</strong> of <strong>{{ formattedPageCount }}</strong>
          </span>
          <button type="button" class="pagination-filter__page-btn" :data-testid="tid('next-btn')" aria-label="Next page" :disabled="disabled || loading || page >= pageCount" @click="changePage(page + 1)">
            <v-icon class="pagination-filter__glyph">mdi-chevron-right</v-icon>
          </button>
          <div class="pagination-filter__page-jump">
            <input
              v-model="pageJumpInput"
              type="text"
              inputmode="numeric"
              class="pagination-filter__input noTransform"
              :data-testid="tid('page-jump-input')"
              :disabled="disabled || loading"
              aria-label="Go to page"
              @keydown.enter.prevent="jumpToPage"
            />
            <button type="button" class="pagination-filter__go-btn" :data-testid="tid('page-jump-btn')" :disabled="disabled || loading || jumpTarget === null" @click="jumpToPage">Go</button>
          </div>
        </div>
        <slot name="toolbar-right" />
      </div>
    </div>

    <div v-if="showFilterModal" ref="filterOverlay" class="pagination-filter__overlay" :data-testid="tid('filter-modal-overlay')" @click.self="closeFilterModal">
      <div ref="filterModal" class="pagination-filter__modal" role="dialog" aria-modal="true" aria-label="Filters" tabindex="-1" :data-testid="tid('filter-modal')" @keydown="onFilterDialogKeydown">
        <div class="pagination-filter__modal-header">
          <h3 class="pagination-filter__title">Filters</h3>
          <button type="button" class="pagination-filter__icon-btn" :data-testid="tid('filter-modal-close')" aria-label="Close filters" @click="closeFilterModal">
            <v-icon class="pagination-filter__glyph">mdi-close</v-icon>
          </button>
        </div>
        <div class="pagination-filter__modal-chrome">
          <div v-if="enableSaved" class="pagination-filter__tabs">
            <button type="button" :data-testid="tid('filter-tab-saved')" :class="['pagination-filter__tab', { 'pagination-filter__tab--active': effectiveFilterTab === tabSaved }]" @click="activeFilterTab = tabSaved">
              Saved Filters
            </button>
            <button type="button" :data-testid="tid('filter-tab-playground')" :class="['pagination-filter__tab', { 'pagination-filter__tab--active': effectiveFilterTab === tabPlayground }]" @click="activeFilterTab = tabPlayground">
              Filter
            </button>
          </div>
          <p class="pagination-filter__note" :data-testid="tid('filter-note')">{{ filterNote }}</p>
        </div>
        <div class="pagination-filter__modal-body">
          <PaginationFilterBuilder
            :key="effectiveFilterTab"
            :test-id-prefix="testIdPrefix"
            :groups="currentFilterGroups"
            :grouping-connector="currentGroupingConnector"
            :fields="filterableFields"
            :disabled="disabled"
            @set-grouping-connector="setGroupingConnector"
            @add-group="addGroup"
            @remove-group="removeGroup"
            @update-group-name="updateGroupName"
            @update-group-connector="updateGroupConnector"
            @update-condition="updateCondition"
            @add-condition="addCondition"
            @remove-condition="removeCondition"
          />
        </div>
        <div class="pagination-filter__actions">
          <button type="button" class="pagination-filter__reset-btn" :data-testid="tid('reset-filters-btn')" :disabled="disabled" @click="resetFilters">
            Reset
          </button>
          <!-- Span receives hover while the disabled Apply button has pointer-events none. -->
          <v-tooltip bottom content-class="tooltip-bottom" :disabled="!loading">
            <template v-slot:activator="{ on, attrs }">
              <span class="pagination-filter__apply-wrap" v-bind="attrs" v-on="on">
                <button type="button" class="pagination-filter__apply-btn" :data-testid="tid('apply-filters-btn')" :disabled="disabled || !filterApplyEnabled" @click="applyFilters">
                  Apply
                </button>
              </span>
            </template>
            <span>{{ applyLoadingTooltip }}</span>
          </v-tooltip>
        </div>
      </div>
    </div>

    <div v-if="showSortPopover" ref="sortPopover" class="pagination-filter__sort-popover" role="dialog" aria-modal="true" aria-label="Sort" tabindex="-1" :data-testid="tid('sort-popover')" @keydown="onSortDialogKeydown">
      <div class="pagination-filter__sort-header">
        <h3 class="pagination-filter__title">Sort</h3>
        <button type="button" class="pagination-filter__icon-btn" :data-testid="tid('sort-popover-close')" aria-label="Close sort" @click="closeSortPopover">
          <v-icon class="pagination-filter__glyph">mdi-close</v-icon>
        </button>
      </div>
      <div class="pagination-filter__modal-chrome">
        <div v-if="enableSaved" class="pagination-filter__tabs pagination-filter__tabs--compact">
          <button type="button" :data-testid="tid('sort-tab-saved')" :class="['pagination-filter__tab', 'pagination-filter__tab--small', { 'pagination-filter__tab--active': effectiveSortTab === tabSaved }]" @click="activeSortTab = tabSaved">
            Saved Sort
          </button>
          <button type="button" :data-testid="tid('sort-tab-playground')" :class="['pagination-filter__tab', 'pagination-filter__tab--small', { 'pagination-filter__tab--active': effectiveSortTab === tabPlayground }]" @click="activeSortTab = tabPlayground">
            Sort
          </button>
        </div>
        <p class="pagination-filter__note" :data-testid="tid('sort-note')">{{ sortNote }}</p>
      </div>
      <div class="pagination-filter__modal-body pagination-filter__modal-body--sort">
        <PaginationFilterSort
          :key="effectiveSortTab"
          :test-id-prefix="testIdPrefix"
          :columns="sortableFields"
          :sort-items="currentSortItems"
          :disabled="disabled"
          @toggle-column="toggleSortColumn"
          @set-direction="setSortDirection"
        />
      </div>
      <div class="pagination-filter__actions">
        <button type="button" class="pagination-filter__reset-btn" :data-testid="tid('reset-sort-btn')" :disabled="disabled" @click="resetSort">Reset</button>
        <v-tooltip bottom content-class="tooltip-bottom" :disabled="!loading">
          <template v-slot:activator="{ on, attrs }">
            <span class="pagination-filter__apply-wrap" v-bind="attrs" v-on="on">
              <button type="button" class="pagination-filter__apply-btn" :data-testid="tid('apply-sort-btn')" :disabled="disabled || !sortApplyEnabled" @click="applySort">
                Apply
              </button>
            </span>
          </template>
          <span>{{ applyLoadingTooltip }}</span>
        </v-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
import PaginationFilterBuilder from './PaginationFilterBuilder.vue';
import PaginationFilterSort from './PaginationFilterSort.vue';
import {
  APPLY_LOADING_TOOLTIP,
  APPLY_SOURCE,
  GROUP_CONNECTOR,
  addConditionInGroups,
  buildFilterPayload,
  buildSortPayload,
  cloneState,
  countValidConditions,
  createDefaultCondition,
  createDefaultFilterGroup,
  emptyFilters,
  filterBadgeCount,
  groupingConnectorOf,
  isSystemFallbackSort,
  playgroundFiltersFromApplied,
  playgroundSortFromApplied,
  parsePageJump,
  patchConditionInGroups,
  patchGroup,
  prepareFilterDraft,
  removeConditionInGroups,
  sameSortPayload,
  sortBadgeCount,
  toEditorFilters,
  toggleSortField,
  withSortDirection,
} from './PaginationFilterUtils.js';

export default {
  name: 'PaginationFilter',

  components: { PaginationFilterBuilder, PaginationFilterSort },

  props: {
    fields: { type: Array, default: () => [] },
    savedFilters: { type: Object, default: () => ({ groups: [], group_connectors: [] }) },
    savedSort: { type: Array, default: () => [] },
    queryFilters: { type: Object, default: () => ({ groups: [], group_connectors: [] }) },
    querySort: { type: Array, default: () => [] },
    totalCount: { type: Number, default: 0 },
    page: { type: Number, default: 1 },
    pageCount: { type: Number, default: 0 },
    /** Accepted so the parent can pass page size. Pagination emits page numbers; the parent owns page size. */
    pageSize: { type: Number, default: 50 },
    loading: { type: Boolean, default: false },
    /** When false, Saved tabs are hidden and Apply never emits save. */
    enableSaved: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    testIdPrefix: { type: String, default: 'pagination-filter' },
  },

  data() {
    return {
      tabSaved: APPLY_SOURCE.SAVED,
      tabPlayground: APPLY_SOURCE.PLAYGROUND,
      showFilterModal: false,
      showSortPopover: false,
      activeFilterTab: APPLY_SOURCE.PLAYGROUND,
      activeSortTab: APPLY_SOURCE.PLAYGROUND,
      savedFiltersDraft: emptyFilters(),
      playgroundFiltersDraft: emptyFilters(),
      savedGroupingConnector: GROUP_CONNECTOR.OR,
      playgroundGroupingConnector: GROUP_CONNECTOR.OR,
      savedSortDraft: [],
      playgroundSortDraft: [],
      appliedFilters: emptyFilters(),
      appliedSort: [],
      criteriaRollback: null,
      returnFocus: null,
      dialogFocusOwner: '',
      hasLocalFilterApply: false,
      hasLocalSortApply: false,
      appliedSystemSort: false,
      appliedFilterSource: null,
      showPlaygroundOnNextOpen: false,
      savedFiltersNeedSync: false,
      savedSortNeedSync: false,
      filterSnapshot: null,
      sortSnapshot: null,
      pageJumpInput: '',
      sortListenersBound: false,
    };
  },

  computed: {
    filterableFields() {
      return (this.fields || []).filter((field) => field && field.key && field.filterable !== false);
    },
    sortableFields() {
      return (this.fields || []).filter((field) => field && field.key && field.sortable !== false);
    },
    firstFieldKey() {
      return this.filterableFields.length ? this.filterableFields[0].key : '';
    },
    effectiveFilterTab() {
      if (!this.enableSaved) return APPLY_SOURCE.PLAYGROUND;
      return this.activeFilterTab === APPLY_SOURCE.SAVED ? APPLY_SOURCE.SAVED : APPLY_SOURCE.PLAYGROUND;
    },
    effectiveSortTab() {
      if (!this.enableSaved) return APPLY_SOURCE.PLAYGROUND;
      return this.activeSortTab === APPLY_SOURCE.SAVED ? APPLY_SOURCE.SAVED : APPLY_SOURCE.PLAYGROUND;
    },
    currentFilterGroups() {
      const draft = this.effectiveFilterTab === APPLY_SOURCE.SAVED ? this.savedFiltersDraft : this.playgroundFiltersDraft;
      return draft && Array.isArray(draft.groups) ? draft.groups : [];
    },
    currentGroupingConnector() {
      return this.effectiveFilterTab === APPLY_SOURCE.SAVED ? this.savedGroupingConnector : this.playgroundGroupingConnector;
    },
    currentSortItems() {
      return this.effectiveSortTab === APPLY_SOURCE.SAVED ? this.savedSortDraft : this.playgroundSortDraft;
    },
    activeFilterCount() {
      return filterBadgeCount(this.queryFilters, this.fields);
    },
    activeSortCount() {
      const applied = buildSortPayload(this.appliedSort);
      if (applied.length) return applied.length;
      return sortBadgeCount(this.querySort, this.savedSort);
    },
    formattedCount() {
      const count = Number(this.totalCount);
      return (Number.isFinite(count) ? count : 0).toLocaleString();
    },
    formattedPageCount() {
      const count = Number(this.pageCount);
      return (Number.isFinite(count) ? count : 0).toLocaleString();
    },
    jumpTarget() {
      return parsePageJump(this.pageJumpInput, this.page, this.pageCount);
    },
    filterNote() {
      if (this.effectiveFilterTab === APPLY_SOURCE.SAVED) {
        return 'This filter is saved for you and is applied when you open Trial Balance.';
      }
      return 'This filter applies only for now and is not saved.';
    },
    sortNote() {
      if (this.effectiveSortTab === APPLY_SOURCE.SAVED) {
        return 'This sort is saved for you and is applied when you open Trial Balance.';
      }
      return 'This sort applies only for now and is not saved.';
    },
    applyLoadingTooltip() {
      return APPLY_LOADING_TOOLTIP;
    },
    filterApplyEnabled() {
      if (this.disabled || this.loading) return false;
      const payload = buildFilterPayload(this.currentFilterGroups, this.currentGroupingConnector, this.fields);
      if (payload.groups.length > 0) return true;
      if (countValidConditions(this.appliedFilters, this.fields) > 0) return true;
      // Reset can empty the draft while a saved preference still exists and the grid is already unfiltered.
      if (this.effectiveFilterTab === APPLY_SOURCE.SAVED) {
        return countValidConditions(this.savedFilters, this.fields) > 0;
      }
      return false;
    },
    sortApplyEnabled() {
      if (this.disabled || this.loading) return false;
      if (buildSortPayload(this.currentSortItems).length > 0) return true;
      if (this.appliedSort.length > 0) return true;
      if (this.effectiveSortTab === APPLY_SOURCE.SAVED) {
        return buildSortPayload(this.savedSort).length > 0;
      }
      return false;
    },
  },

  watch: {
    savedFilters: {
      deep: true,
      handler() {
        if (this.showFilterModal) {
          this.savedFiltersNeedSync = true;
          return;
        }
        this.syncSavedFilters();
      },
    },
    savedSort: {
      deep: true,
      handler() {
        if (this.showSortPopover) {
          this.savedSortNeedSync = true;
          return;
        }
        this.syncSavedSort();
      },
    },
    fields: {
      deep: true,
      handler() {
        if (!this.showFilterModal) {
          prepareFilterDraft(this.savedFiltersDraft, this.fields, this.firstFieldKey);
          prepareFilterDraft(this.playgroundFiltersDraft, this.fields, this.firstFieldKey);
        }
      },
    },
    enableSaved(enabled) {
      if (enabled) return;
      this.activeFilterTab = APPLY_SOURCE.PLAYGROUND;
      this.activeSortTab = APPLY_SOURCE.PLAYGROUND;
    },
  },

  created() {
    this.overlayHomes = Object.create(null);
    this.syncSavedFilters();
    this.playgroundFiltersDraft = prepareFilterDraft(toEditorFilters(emptyFilters(), this.firstFieldKey), this.fields, this.firstFieldKey);
    this.syncSavedSort();
    this.activeFilterTab = this.initialFilterTab();
    this.activeSortTab = this.initialSortTab();
  },

  mounted() {
    document.addEventListener('keydown', this.handleEscape);
  },

  beforeDestroy() {
    this.unbindSortListeners();
    document.removeEventListener('keydown', this.handleEscape);
    this.restoreOverlay('filterOverlay');
    this.restoreOverlay('sortPopover');
    this.returnFocus = null;
    this.dialogFocusOwner = '';
    this.criteriaRollback = null;
    this.showFilterModal = false;
    this.showSortPopover = false;
    this.filterSnapshot = null;
    this.sortSnapshot = null;
    this.savedFiltersDraft = emptyFilters();
    this.playgroundFiltersDraft = emptyFilters();
    this.savedSortDraft = [];
    this.playgroundSortDraft = [];
    this.appliedFilters = emptyFilters();
    this.appliedSort = [];
    this.pageJumpInput = '';
    this.hasLocalFilterApply = false;
    this.hasLocalSortApply = false;
    this.appliedSystemSort = false;
    this.appliedFilterSource = null;
    this.showPlaygroundOnNextOpen = false;
    this.savedFiltersNeedSync = false;
    this.savedSortNeedSync = false;
  },

  methods: {
    tid(name) {
      return `${this.testIdPrefix}__${name}`;
    },

    initialFilterTab() {
      if (!this.enableSaved) return APPLY_SOURCE.PLAYGROUND;
      const groups = this.savedFilters && this.savedFilters.groups;
      return groups && groups.length ? APPLY_SOURCE.SAVED : APPLY_SOURCE.PLAYGROUND;
    },

    initialSortTab() {
      if (!this.enableSaved) return APPLY_SOURCE.PLAYGROUND;
      return buildSortPayload(this.savedSort).length ? APPLY_SOURCE.SAVED : APPLY_SOURCE.PLAYGROUND;
    },

    syncSavedFilters() {
      this.savedFiltersNeedSync = false;
      this.savedGroupingConnector = groupingConnectorOf(this.savedFilters);
      this.savedFiltersDraft = prepareFilterDraft(toEditorFilters(this.savedFilters, this.firstFieldKey), this.fields, this.firstFieldKey);
    },

    syncSavedSort() {
      this.savedSortNeedSync = false;
      this.savedSortDraft = buildSortPayload(this.savedSort);
      this.releaseSavedSortFromApplied();
    },

    releaseSavedSortFromApplied() {
      if (this.criteriaRollback) return;
      if (!buildSortPayload(this.appliedSort).length) return;
      if (!sameSortPayload(this.appliedSort, this.savedSort)) return;
      this.appliedSort = [];
      if (!this.showSortPopover) this.hydratePlaygroundSort();
    },

    /** A saved-view reload drops playground criteria. Saved defaults stay on the Saved tab. */
    allowSavedSync() {
      this.hasLocalFilterApply = false;
      this.hasLocalSortApply = false;
      this.appliedSystemSort = false;
      this.criteriaRollback = null;
      this.appliedFilterSource = null;
      this.appliedFilters = emptyFilters();
      this.blankPlaygroundDraft();
      this.appliedSort = [];
      this.blankPlaygroundSort();
    },

    clearAppliedFilters() {
      this.hasLocalFilterApply = true;
      this.appliedFilterSource = null;
      this.criteriaRollback = null;
      this.appliedFilters = emptyFilters();
      this.blankPlaygroundDraft();
      this.pinPlaygroundSnapshot();
      this.showPlaygroundOnNextOpen = true;
    },

    /** One blank playground row. Does not read saved defaults. */
    blankPlaygroundDraft() {
      this.playgroundGroupingConnector = GROUP_CONNECTOR.OR;
      this.playgroundFiltersDraft = prepareFilterDraft(
        toEditorFilters(emptyFilters(), this.firstFieldKey),
        this.fields,
        this.firstFieldKey,
      );
    },

    /** Keep a cleared playground across close. Close restores this snapshot. */
    pinPlaygroundSnapshot() {
      if (!this.filterSnapshot) return;
      this.filterSnapshot = {
        ...this.filterSnapshot,
        playgroundFiltersDraft: cloneState(this.playgroundFiltersDraft),
        playgroundGroupingConnector: this.playgroundGroupingConnector,
      };
    },

    /**
     * Fill an empty playground from appliedFilters only.
     * appliedFilters is null when the query is the saved view, so this stays empty.
     */
    seedPlaygroundFilters() {
      const applied = playgroundFiltersFromApplied(this.appliedFilters);
      if (countValidConditions(this.playgroundFiltersDraft, this.fields) > 0) return;
      if (countValidConditions(applied, this.fields) === 0) return;
      this.playgroundFiltersDraft = prepareFilterDraft(toEditorFilters(applied, this.firstFieldKey), this.fields, this.firstFieldKey);
      this.playgroundGroupingConnector = groupingConnectorOf(applied);
    },

    /** Replace the playground draft with the committed applied filters. Saved defaults are not a source. */
    hydratePlaygroundFromApplied() {
      const applied = playgroundFiltersFromApplied(this.appliedFilters);
      if (countValidConditions(applied, this.fields) === 0) {
        this.blankPlaygroundDraft();
        return;
      }
      this.playgroundGroupingConnector = groupingConnectorOf(applied);
      this.playgroundFiltersDraft = prepareFilterDraft(toEditorFilters(applied, this.firstFieldKey), this.fields, this.firstFieldKey);
    },

    blankPlaygroundSort() {
      this.playgroundSortDraft = [];
    },

    pinPlaygroundSortSnapshot() {
      if (!this.sortSnapshot) return;
      this.sortSnapshot = {
        ...this.sortSnapshot,
        playgroundSortDraft: cloneState(this.playgroundSortDraft),
      };
    },

    seedPlaygroundSort() {
      if (this.playgroundSortDraft.length > 0 || !this.appliedSort.length) return;
      this.playgroundSortDraft = cloneState(this.appliedSort);
    },

    hydratePlaygroundSort() {
      if (!this.appliedSort.length) {
        this.blankPlaygroundSort();
        return;
      }
      this.playgroundSortDraft = cloneState(this.appliedSort);
    },

    toggleFilter() {
      if (this.disabled) return;
      if (this.showFilterModal) {
        this.closeFilterModal(true);
        return;
      }
      // Dismiss Sort on its normal close path, then open Filters after that close flushes.
      if (this.showSortPopover) {
        this.captureFocus('filter');
        this.closeSortPopover(true);
        this.$nextTick(() => {
          if (this._isDestroyed || this._isBeingDestroyed) return;
          if (this.showSortPopover || this.showFilterModal) return;
          this.openFilterModal();
        });
        return;
      }
      this.openFilterModal();
    },

    openFilterModal() {
      this.captureFocus('filter');
      this.closeSortPopover(true);
      this.seedPlaygroundFilters();
      if (!this.savedFiltersDraft.groups || !this.savedFiltersDraft.groups.length) {
        this.savedFiltersDraft = toEditorFilters(emptyFilters(), this.firstFieldKey);
      }
      const openPlayground = this.showPlaygroundOnNextOpen;
      this.showPlaygroundOnNextOpen = false;
      this.activeFilterTab = openPlayground ? APPLY_SOURCE.PLAYGROUND : this.initialFilterTab();
      this.filterSnapshot = {
        savedFiltersDraft: cloneState(this.savedFiltersDraft),
        playgroundFiltersDraft: cloneState(this.playgroundFiltersDraft),
        savedGroupingConnector: this.savedGroupingConnector,
        playgroundGroupingConnector: this.playgroundGroupingConnector,
      };
      this.showFilterModal = true;
      this.$nextTick(() => {
        if (!this.showFilterModal) return;
        this.mountOverlay('filterOverlay');
        this.focusDialog('filterModal');
      });
    },

    /** Click-away restores the open snapshot. Apply passes false so the committed draft stays. */
    closeFilterModal(restore) {
      if (restore !== false && this.filterSnapshot) {
        this.savedFiltersDraft = cloneState(this.filterSnapshot.savedFiltersDraft);
        this.playgroundFiltersDraft = cloneState(this.filterSnapshot.playgroundFiltersDraft);
        this.savedGroupingConnector = this.filterSnapshot.savedGroupingConnector;
        this.playgroundGroupingConnector = this.filterSnapshot.playgroundGroupingConnector;
      }
      this.filterSnapshot = null;
      this.restoreOverlay('filterOverlay');
      this.showFilterModal = false;
      if (restore !== false && this.savedFiltersNeedSync) this.syncSavedFilters();
      else this.savedFiltersNeedSync = false;
      if (this.dialogFocusOwner === 'filter') this.restoreDialogFocus();
    },

    /**
     * Move a fixed overlay to document.body so a transformed page ancestor
     * cannot shrink it. Put it back before close so it is not left on body.
     */
    mountOverlay(refName) {
      const el = this.$refs[refName];
      if (!el || el.parentElement === document.body) return;
      this.overlayHomes[refName] = el.parentNode;
      document.body.appendChild(el);
    },

    restoreOverlay(refName) {
      const el = this.$refs[refName];
      const home = this.overlayHomes[refName];
      if (el && home && el.parentElement === document.body) {
        home.appendChild(el);
      }
      this.overlayHomes[refName] = null;
    },

    toggleSort() {
      if (this.disabled) return;
      if (this.showSortPopover) {
        this.closeSortPopover(true);
        return;
      }
      this.openSortPopover();
    },

    openSortPopover() {
      this.captureFocus('sort');
      this.closeFilterModal(true);
      this.seedPlaygroundSort();
      this.activeSortTab = this.initialSortTab();
      this.sortSnapshot = {
        savedSortDraft: cloneState(this.savedSortDraft),
        playgroundSortDraft: cloneState(this.playgroundSortDraft),
      };
      this.showSortPopover = true;
      this.$nextTick(() => {
        if (!this.showSortPopover) return;
        this.mountOverlay('sortPopover');
        this.positionSortPopover();
        this.bindSortListeners();
        this.focusDialog('sortPopover');
      });
    },

    /** Same restore rule as closeFilterModal. */
    closeSortPopover(restore) {
      if (restore !== false && this.sortSnapshot) {
        this.savedSortDraft = cloneState(this.sortSnapshot.savedSortDraft);
        this.playgroundSortDraft = cloneState(this.sortSnapshot.playgroundSortDraft);
      }
      this.sortSnapshot = null;
      this.restoreOverlay('sortPopover');
      this.showSortPopover = false;
      if (restore !== false && this.savedSortNeedSync) this.syncSavedSort();
      else this.savedSortNeedSync = false;
      this.unbindSortListeners();
      if (this.dialogFocusOwner === 'sort') this.restoreDialogFocus();
    },

    bindSortListeners() {
      if (this.sortListenersBound) return;
      this.sortListenersBound = true;
      document.addEventListener('click', this.handleSortClickOutside);
      window.addEventListener('resize', this.positionSortPopover);
      window.addEventListener('scroll', this.positionSortPopover, true);
    },

    unbindSortListeners() {
      if (!this.sortListenersBound) return;
      this.sortListenersBound = false;
      document.removeEventListener('click', this.handleSortClickOutside);
      window.removeEventListener('resize', this.positionSortPopover);
      window.removeEventListener('scroll', this.positionSortPopover, true);
    },

    handleSortClickOutside(event) {
      const popover = this.$refs.sortPopover;
      const button = this.$refs.sortBtn;
      const target = event.target;
      if (popover && popover.contains(target)) return;
      if (button && button.contains(target)) return;
      this.closeSortPopover(true);
    },

    handleEscape(event) {
      if (event.key !== 'Escape') return;
      if (this.showFilterModal) {
        this.closeFilterModal(true);
        return;
      }
      if (this.showSortPopover) this.closeSortPopover(true);
    },

    /** Pin the popover under the sort button. Gap comes from margin-top in CSS. */
    positionSortPopover() {
      const button = this.$refs.sortBtn;
      const popover = this.$refs.sortPopover;
      if (!button || !popover) return;
      const rect = button.getBoundingClientRect();
      let left = rect.left;
      if (left + popover.offsetWidth > window.innerWidth) left = Math.max(0, rect.right - popover.offsetWidth);
      popover.style.top = `${rect.bottom}px`;
      popover.style.left = `${left}px`;
    },

    setFilterDraft(groups) {
      if (this.effectiveFilterTab === APPLY_SOURCE.SAVED) {
        this.savedFiltersDraft = { ...this.savedFiltersDraft, groups };
        return;
      }
      this.playgroundFiltersDraft = { ...this.playgroundFiltersDraft, groups };
    },

    setGroupingConnector(connector) {
      const next = connector === GROUP_CONNECTOR.AND ? GROUP_CONNECTOR.AND : GROUP_CONNECTOR.OR;
      if (this.effectiveFilterTab === APPLY_SOURCE.SAVED) this.savedGroupingConnector = next;
      else this.playgroundGroupingConnector = next;
    },

    addGroup() {
      this.setFilterDraft(this.currentFilterGroups.concat([createDefaultFilterGroup(this.firstFieldKey)]));
    },

    removeGroup(groupId) {
      if (this.currentFilterGroups.length <= 1) {
        this.resetFilters();
        return;
      }
      this.setFilterDraft(this.currentFilterGroups.filter((group) => group.id !== groupId));
      if (this.effectiveFilterTab !== APPLY_SOURCE.PLAYGROUND) return;
      if (countValidConditions(this.playgroundFiltersDraft, this.fields) > 0) return;
      this.persistEmptyPlayground();
    },

    updateGroupName(groupId, name) {
      this.setFilterDraft(patchGroup(this.currentFilterGroups, groupId, { name }));
    },

    updateGroupConnector(groupId, connector) {
      const next = connector === GROUP_CONNECTOR.OR ? GROUP_CONNECTOR.OR : GROUP_CONNECTOR.AND;
      this.setFilterDraft(patchGroup(this.currentFilterGroups, groupId, { connector: next }));
    },

    updateCondition(groupId, conditionId, field, value) {
      this.setFilterDraft(patchConditionInGroups(this.currentFilterGroups, groupId, conditionId, field, value, this.fields));
    },

    addCondition(groupId) {
      this.setFilterDraft(addConditionInGroups(this.currentFilterGroups, groupId, createDefaultCondition(this.firstFieldKey)));
    },

    removeCondition(groupId, conditionId) {
      this.setFilterDraft(removeConditionInGroups(this.currentFilterGroups, groupId, conditionId, createDefaultCondition(this.firstFieldKey)));
      if (this.effectiveFilterTab !== APPLY_SOURCE.PLAYGROUND) return;
      if (countValidConditions(this.playgroundFiltersDraft, this.fields) > 0) return;
      this.persistEmptyPlayground();
    },

    /**
     * Saved Reset stays a draft until Apply, so close puts the saved preference back.
     * Playground Reset commits an empty playground. Close must not restore the previous draft.
     */
    resetFilters() {
      if (this.effectiveFilterTab === APPLY_SOURCE.SAVED) {
        this.savedFiltersDraft = toEditorFilters(emptyFilters(), this.firstFieldKey);
        this.savedGroupingConnector = GROUP_CONNECTOR.OR;
        return;
      }
      this.persistEmptyPlayground();
    },

    /**
     * Empty the playground and keep that empty draft in the open snapshot.
     * When a playground filter is on the query, emit apply so the list clears too.
     */
    persistEmptyPlayground() {
      const hadApplied = countValidConditions(this.appliedFilters, this.fields) > 0;
      if (hadApplied) {
        this.rememberCriteria();
        this.criteriaRollback.playgroundFiltersDraft = cloneState(this.playgroundFiltersDraft);
        this.criteriaRollback.playgroundGroupingConnector = this.playgroundGroupingConnector;
      }
      this.blankPlaygroundDraft();
      this.pinPlaygroundSnapshot();
      this.appliedFilters = emptyFilters();
      this.hasLocalFilterApply = true;
      this.appliedFilterSource = null;
      if (!hadApplied) return;
      const applyPayload = { filters: emptyFilters(), source: APPLY_SOURCE.PLAYGROUND };
      if (buildSortPayload(this.appliedSort).length) applyPayload.sort = cloneState(this.appliedSort);
      console.log('[PaginationFilter]:', { event: 'apply', source: APPLY_SOURCE.PLAYGROUND, conditions: 0, sorts: buildSortPayload(applyPayload.sort).length });
      this.$emit('apply', applyPayload);
    },

    /** Saved Apply emits save and apply. Playground Apply emits apply only, with the sort already applied. */
    applyFilters() {
      if (!this.filterApplyEnabled) return;
      this.rememberCriteria();
      const source = this.effectiveFilterTab;
      this.appliedFilterSource = source;
      const filters = buildFilterPayload(this.currentFilterGroups, this.currentGroupingConnector, this.fields);
      const applyPayload = { filters: cloneState(filters), source };
      if (buildSortPayload(this.appliedSort).length) applyPayload.sort = cloneState(this.appliedSort);
      console.log('[PaginationFilter]:', { event: source === APPLY_SOURCE.SAVED ? 'save-and-apply' : 'apply', source, conditions: countValidConditions(filters, this.fields), sorts: buildSortPayload(applyPayload.sort).length });
      if (source === APPLY_SOURCE.SAVED) this.$emit('save', { filters: cloneState(filters) });
      this.$emit('apply', applyPayload);
      this.closeFilterModal(false);
    },

    setSortDraft(items) {
      if (this.effectiveSortTab === APPLY_SOURCE.SAVED) this.savedSortDraft = items;
      else this.playgroundSortDraft = items;
    },

    toggleSortColumn(field) {
      this.setSortDraft(toggleSortField(this.currentSortItems, field));
    },

    setSortDirection(field, direction) {
      this.setSortDraft(withSortDirection(this.currentSortItems, field, direction));
    },

    resetSort() {
      if (this.effectiveSortTab === APPLY_SOURCE.SAVED) {
        this.savedSortDraft = [];
        return;
      }
      this.persistEmptyPlaygroundSort();
    },

    persistEmptyPlaygroundSort() {
      const hadApplied = buildSortPayload(this.appliedSort).length > 0;
      if (hadApplied) {
        this.rememberCriteria();
        this.criteriaRollback.playgroundSortDraft = cloneState(this.playgroundSortDraft);
      }
      this.blankPlaygroundSort();
      this.pinPlaygroundSortSnapshot();
      this.appliedSort = [];
      this.hasLocalSortApply = true;
      if (!hadApplied) return;
      const filters = cloneState(this.appliedFilters);
      const appliedCount = countValidConditions(filters, this.fields);
      const applyPayload = { sort: [] };
      if (appliedCount > 0) applyPayload.filters = filters;
      if (appliedCount > 0 && this.appliedFilterSource) applyPayload.source = this.appliedFilterSource;
      console.log('[PaginationFilter]:', { event: 'apply', source: APPLY_SOURCE.PLAYGROUND, conditions: appliedCount, sorts: 0 });
      this.$emit('apply', applyPayload);
    },

    applySort() {
      if (!this.sortApplyEnabled) return;
      this.rememberCriteria();
      const sortSource = this.effectiveSortTab;
      const sort = buildSortPayload(this.currentSortItems);
      const filters = cloneState(this.appliedFilters);
      const source = this.appliedFilterSource || undefined;
      const appliedCount = countValidConditions(filters, this.fields);
      console.log('[PaginationFilter]:', { event: sortSource === APPLY_SOURCE.SAVED ? 'save-and-apply' : 'apply', source: sortSource, filterSource: source, conditions: appliedCount, sorts: sort.length });
      if (sortSource === APPLY_SOURCE.SAVED) this.$emit('save', { sort: cloneState(sort) });
      this.appliedSystemSort = sortSource !== APPLY_SOURCE.SAVED && isSystemFallbackSort(sort);
      const applyPayload = { sort: cloneState(sort) };
      if (appliedCount > 0) applyPayload.filters = filters;
      if (source) applyPayload.source = source;
      if (sortSource !== APPLY_SOURCE.SAVED && sort.length === 0) {
        this.appliedSort = [];
        this.blankPlaygroundSort();
        this.pinPlaygroundSortSnapshot();
      }
      this.$emit('apply', applyPayload);
      this.closeSortPopover(false);
    },

    rememberCriteria() {
      this.criteriaRollback = {
        appliedFilters: cloneState(this.appliedFilters),
        appliedSort: cloneState(this.appliedSort),
        appliedFilterSource: this.appliedFilterSource,
      };
      this.hasLocalFilterApply = true;
      this.hasLocalSortApply = true;
    },

    /** Parent calls this after the page request fails. The badge returns to the rows still on screen. */
    rollbackCriteria() {
      const snap = this.criteriaRollback;
      if (!snap) return;
      this.appliedFilters = snap.appliedFilters;
      this.appliedSort = snap.appliedSort;
      this.appliedFilterSource = snap.appliedFilterSource;
      this.appliedSystemSort = false;
      if (snap.playgroundFiltersDraft) {
        this.playgroundFiltersDraft = snap.playgroundFiltersDraft;
        this.playgroundGroupingConnector = snap.playgroundGroupingConnector;
        this.pinPlaygroundSnapshot();
      }
      if (snap.playgroundSortDraft) {
        this.playgroundSortDraft = snap.playgroundSortDraft;
        this.pinPlaygroundSortSnapshot();
      }
      this.criteriaRollback = null;
    },

    commitApplied(filters, sort) {
      const applied = playgroundFiltersFromApplied(filters);
      this.appliedFilters = cloneState(applied);
      this.appliedSort = playgroundSortFromApplied(sort, this.savedSort, this.appliedSystemSort);
      this.appliedSystemSort = false;
      this.hasLocalFilterApply = true;
      this.hasLocalSortApply = true;
      this.criteriaRollback = null;
      if (countValidConditions(applied, this.fields) === 0) this.appliedFilterSource = null;
      if (!this.showFilterModal) this.hydratePlaygroundFromApplied();
      if (!this.showSortPopover) this.hydratePlaygroundSort();
    },

    /** A failed save puts the Saved tab back on the last persisted preference. */
    restoreSavedDraft() {
      this.savedGroupingConnector = groupingConnectorOf(this.savedFilters);
      this.savedFiltersDraft = prepareFilterDraft(
        toEditorFilters(this.savedFilters, this.firstFieldKey),
        this.fields,
        this.firstFieldKey,
      );
      this.savedSortDraft = buildSortPayload(this.savedSort);
    },

    captureFocus(owner) {
      this.dialogFocusOwner = owner;
      const active = document.activeElement;
      this.returnFocus = active && typeof active.focus === 'function' ? active : null;
    },

    restoreDialogFocus() {
      const el = this.returnFocus;
      this.returnFocus = null;
      this.dialogFocusOwner = '';
      if (el && typeof el.focus === 'function') el.focus();
    },

    focusDialog(refName) {
      const root = this.$refs[refName];
      if (root && typeof root.focus === 'function') root.focus();
    },

    /** Tab cycles inside the dialog. The field menus attach inside it, so they stay in the cycle. */
    keepFocusInside(event, root) {
      if (!root || event.key !== 'Tab') return;
      const nodes = root.querySelectorAll('button, [href], input, select, textarea, [tabindex]');
      const list = Array.prototype.filter.call(nodes, (el) => {
        if (el.disabled || el.getAttribute('tabindex') === '-1' || el.getAttribute('aria-hidden') === 'true') return false;
        return el.getClientRects().length > 0;
      });
      if (!list.length) return;
      const first = list[0];
      const last = list[list.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === root || !root.contains(active))) {
        event.preventDefault();
        last.focus();
        return;
      }
      if (!event.shiftKey && (active === last || !root.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    },

    onFilterDialogKeydown(event) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.closeFilterModal(true);
        return;
      }
      this.keepFocusInside(event, this.$refs.filterModal);
    },

    onSortDialogKeydown(event) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.closeSortPopover(true);
        return;
      }
      this.keepFocusInside(event, this.$refs.sortPopover);
    },

    changePage(next) {
      if (this.disabled || this.loading) return;
      if (!Number.isInteger(next) || next < 1 || next > this.pageCount || next === this.page) return;
      this.$emit('page-change', next);
    },

    jumpToPage() {
      if (this.jumpTarget === null || this.disabled || this.loading) return;
      this.$emit('page-change', this.jumpTarget);
      this.pageJumpInput = '';
    },
  },
};
</script>

<style lang="scss" scoped>
@import './PaginationFilter.scss';
</style>

<style lang="scss">
/* Menu content is moved to the app root, outside this component's scope. */
.pagination-filter__select-menu .v-list-item__title {
  font-size: var(--font-size-xxs);
  text-transform: none;
  color: var(--color-text-primary);
}
</style>
